import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { calculateOverallMaturity } from '@/utils/maturityLevelUtils';
import { TrendingUp, Target } from 'lucide-react';
import type { FreeSurveyResponse } from './FreeSurveyDialog';

interface FreeSurveyDetailProps {
  response: FreeSurveyResponse;
}

export default function FreeSurveyDetail({ response }: FreeSurveyDetailProps) {
  const maturityResults = useMemo(() => {
    const cleanScores: Record<string, { current: number; target: number }> = {};
    Object.entries(response.scoresByCategory).forEach(([id, s]) => {
      cleanScores[id] = { current: s.current, target: s.target };
    });
    return calculateOverallMaturity(cleanScores);
  }, [response.scoresByCategory]);

  const categoryScores = Object.entries(response.scoresByCategory);

  // Extract recommendations from answer details (stored data)
  const recommendations = useMemo(() => {
    const recs: Array<{ questionText: string; text: string; priority: string }> = [];
    Object.entries(response.answerDetails || {}).forEach(([, detail]: [string, any]) => {
      const current = detail?.current;
      if (current?.recommendedActions) {
        current.recommendedActions.forEach((action: any) => {
          recs.push({
            questionText: detail.questionText || '',
            text: action.text,
            priority: action.priority || 'medium',
          });
        });
      }
    });
    return recs;
  }, [response.answerDetails]);

  return (
    <div className="space-y-6">
      {/* Maturity Summary */}
      {response.isMaturityModel && maturityResults && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" /> Current Level
                </div>
                <div className="text-2xl font-bold text-foreground">{maturityResults.current?.label || 'N/A'}</div>
                <div className="text-xs text-muted-foreground">Score: {maturityResults.current?.score?.toFixed(1) || '0'}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <Target className="h-4 w-4" /> Target Level
                </div>
                <div className="text-2xl font-bold text-foreground">{maturityResults.target?.label || 'N/A'}</div>
                <div className="text-xs text-muted-foreground">Score: {maturityResults.target?.score?.toFixed(1) || '0'}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Scores */}
      {categoryScores.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Scores by Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {categoryScores.map(([categoryId, scores]) => {
              const categoryName = (scores as any).categoryName || categoryId.slice(0, 8);

              return (
                <div key={categoryId} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <span className="text-sm font-medium text-foreground">{categoryName}</span>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground">Current: <span className="font-medium text-foreground">{scores.current?.toFixed(1)}</span></span>
                    <span className="text-muted-foreground">Target: <span className="font-medium text-primary">{scores.target?.toFixed(1)}</span></span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recommendations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
                <Badge 
                  variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'default' : 'secondary'}
                  className="text-xs mt-0.5 shrink-0"
                >
                  {rec.priority}
                </Badge>
                <div>
                  <p className="text-sm text-foreground">{rec.text}</p>
                  {rec.questionText && (
                    <p className="text-xs text-muted-foreground mt-0.5">{rec.questionText}</p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {recommendations.length === 0 && categoryScores.length === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          No detailed data available for this survey.
        </div>
      )}
    </div>
  );
}
